import { CommonModule } from '@angular/common'
import { HttpClient } from '@angular/common/http'
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChange,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormsModule } from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import { MatChipsModule } from '@angular/material/chips'
import { MatDialog } from '@angular/material/dialog'
import { MatIconModule } from '@angular/material/icon'
import { MatMenuModule } from '@angular/material/menu'
import { ProgressLoaderComponent } from '../../common/progress-loader/progress-loader.component'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { MatTooltipModule } from '@angular/material/tooltip'
import { ActivatedRoute, Router } from '@angular/router'
import { HocuspocusProvider } from '@hocuspocus/provider'
import { injectQueryClient } from '../../query'
import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { AnyExtension, ChainedCommands, Editor, isTextSelection, JSONContent } from '@tiptap/core'
import CharacterCount from '@tiptap/extension-character-count'
import Collaboration from '@tiptap/extension-collaboration'
import FindAndReplace from '@tiptap/extension-find-and-replace'
import { Mark as ProseMirrorMark } from '@tiptap/pm/model'
import { EditorState, PluginKey, TextSelection, Transaction } from '@tiptap/pm/state'
import { EditorView } from '@tiptap/pm/view'
import { TiptapBubbleMenuDirective, TiptapEditorDirective } from 'ngx-tiptap'
import { BehaviorSubject, combineLatest, filter, lastValueFrom, map, Observable, Subscription, switchMap, take } from 'rxjs'

import * as Y from 'yjs'
import { swapListType } from '../../../../../backend/src/tiptap/editor/extensions/nodes/list'
import {
  COLOR_ATTRIBUTE_NAME,
  ColorChange,
  FormattingChanges,
  LIST_ITEM_NODE_NAME,
  ListChange,
  SUGGESTION_MARK_NAME,
  SUGGESTION_TYPE_DELETION,
  SUGGESTION_TYPE_FORMATTING,
  SUGGESTION_TYPE_INSERTION,
  TEXT_STYLE_MARK_NAME,
} from '../../../../../backend/src/tiptap/editor/extensions/marks/suggestion'
import { COMMENT_ANCHOR_MARK_NAME } from '../../../../../backend/src/tiptap/editor/extensions/marks/comment-anchor'
import { EDITOR_STRUCTURAL_EXTENSIONS } from '../../../../../backend/src/tiptap/editor-extensions'
import { Call, isMinutesGenerationTargeted, MetricType } from '../../calls.service'
import { LoggerService } from '../../logger.service'
import { MetricsService } from '../../metrics.service'
import { queryKeys } from '../../query-keys'
import { ToastService } from '../../toast.service'
import { MeUser, UsersService } from '../../users.service'
import { constructNameForUser, stringArraysEqual } from '../../utils'
import { AiActionFactCheckDialogComponent, AiActionFactCheckDialogData } from './ai-actions/ai-action-fact-check-dialog.component'
import { AiActionStreamingDialogComponent, AiActionStreamingDialogData } from './ai-actions/ai-action-streaming-dialog.component'
import { AI_ACTION_CONFIGS, LlmAction, SentenceIdPair } from './ai-actions/ai-action.types'
import {
  expandSelectionToWordBoundaries,
  getExistingItemContent,
  getSelectedText,
  getSentenceIdPairsFromSelection,
  getTemplateItemIdsFromSelection,
} from './ai-actions/selection-utils'
import { EditorSettingsService } from '../editor-settings.service'
import { AgendaMissingWarningComponent } from '../agenda-missing-warning/agenda-missing-warning.component'
import { anchorKey, canResolveThread, EditorThread, EditorThreadAnchorType, SidebarFilter } from './editor-thread.types'
import { EditorThreadsService } from './editor-threads.service'
import type { CitationMinuteNodes } from '../../../../../backend/src/citations/minutes-citations-api.dto'
import { buildMinutesCitationExtension } from './extensions/citation-extension'
import { runReplaceAllWithTracking, runReplaceWithTracking } from './extensions/find-replace-track-changes'
import { LockedItemsExtension, refreshLockedItems } from './extensions/locked-items-extension'
import { InsertionPulseExtension } from './extensions/insertion-pulse-extension'
import { pulseInsertedRange } from './extensions/insertion-pulse-plugin'
import { coversLockedRange, findLockedRanges, haveSameLockedItems, overlapsLockedRange } from './extensions/locked-items-plugin'
import { AttendeeStructureExtension } from './extensions/attendee-structure-extension'
import { replaceWithAuthoritativeContent } from './extensions/authoritative-content'
import { EditorRevisionsService, RevisionDownloadEvent, RevisionSummary } from './editor-revisions.service'
import { TiptapRevisionHistoryComponent } from './tiptap-revision-history/tiptap-revision-history.component'
import { ExportScopeExtension } from './extensions/export-scope-extension'
import { openCitedMeeting } from './extensions/open-cited-meeting'
import { ProtectRestrictedMounts } from './extensions/protect-minute-structure'
import { RestrictedSectionNodeView } from './extensions/restricted-section-node-view'
import { MinutesSkeletonComponent } from './minutes-skeleton/minutes-skeleton.component'
import { buildCaretUser, UniqueUserCollaborationCaret } from './extensions/unique-user-collaboration-caret'
import { isAttendeeListSelected } from './extensions/attendee-structure-plugin'
import { MinuteTreeExtension } from './extensions/minute-tree-extension'
import { isMinuteTreeListTypeLocked } from './extensions/minute-tree-plugin'
import { citationPluginKey, OpenDocumentEvent, OpenMeetingEvent } from './extensions/citation-plugin'
import { runColorCommandWithTracking } from './extensions/color-track-changes'
import {
  expandDeletionToEmptiedListItem,
  liftListItemSingleLevel,
  listChangeArraysEqual,
  restoreListItemAtSource,
  runListCommandWithTracking,
} from './extensions/list-track-changes'
import { TrackChangesExtension } from './extensions/track-changes-extension'
import { TRACK_CHANGES_SKIP_META, trackChangesPluginKey } from './extensions/track-changes-plugin'
import {
  collectAllSuggestions,
  collectSuggestionContent,
  collectSuggestionStartPositions,
  colorChangesEqual,
  findAllRangesForSuggestion,
  findSuggestionAtPosition,
  suggestionRunsEqual,
  SuggestionSummary,
  suggestionSummariesEqual,
  SuggestionTextRun,
} from './extensions/track-changes-utils'
import {
  collectCommentAnchors,
  collectCommentAnchorStartPositions,
  collectCommentAnchorSummaries,
  CommentAnchorSummary,
  commentAnchorSummariesEqual,
  findCommentAnchorIdAtPosition,
} from './extensions/comment-anchor-utils'
import { deriveEditorDisplayState, EditorDisplayState } from './editor-display-state'
import { readTrackChangesEnabled, writeTrackChangesEnabled } from './track-changes-storage'
import { SseSyncProvider, STATELESS_REFETCH_CALL_MESSAGE_TYPE } from './sse-sync-provider'
import { CommentPopupComponent } from './comment-popup/comment-popup.component'
import { FindReplacePanelComponent } from './find-replace-panel/find-replace-panel.component'
import { MultiEditorFindReplaceTarget, SearchSegment } from './find-replace-panel/find-replace-target'
import { SuggestionPopupComponent } from './suggestion-popup/suggestion-popup.component'
import { TiptapEditorSidebarComponent } from './tiptap-editor-sidebar/tiptap-editor-sidebar.component'
import { centerViewOnPosition } from './utils/center-view-on-position'
import { TEXT_COLOR_OPTIONS } from './utils/text-colors'

type ActiveSuggestion = {
  suggestionId: string
  authorName: string
  createdAt: string | null
  insertionRuns: SuggestionTextRun[]
  deletionRuns: SuggestionTextRun[]
  formattingAdded: string[]
  formattingRemoved: string[]
  listChanges: ListChange[]
  colorChange: ColorChange | null
}

function navigationIncludes(filter: SidebarFilter): { changes: boolean; comments: boolean } {
  return { changes: filter !== 'comments', comments: filter !== 'changes' }
}

const STATELESS_REVISION_UPDATE_MESSAGE_TYPE = 'revision-update'
const STATELESS_THREAD_UPDATE_PREFIX = 'thread-update:'

const SUGGESTION_POPUP_EDGE_PADDING = 8
// Mirrors the `max-height` cap in suggestion-popup.component.scss so the inline
// style only ever tightens the height — never overrides the SCSS cap on tall editors.
const SUGGESTION_POPUP_MAX_HEIGHT_CAP = 'min(30vh, 210px)'

type SuggestionPopupOptions = {
  strategy: 'fixed' | 'absolute'
  scrollTarget?: HTMLElement
  flip?: boolean
  shift?: { padding?: number }
  size?: { padding?: number; apply?: (args: { availableHeight: number; elements: { floating: HTMLElement } }) => void }
}

function buildSuggestionPopupOptions(getScrollTarget?: () => HTMLElement | undefined): SuggestionPopupOptions {
  return {
    strategy: 'absolute',
    get scrollTarget() {
      return getScrollTarget?.()
    },
    flip: true,
    shift: { padding: SUGGESTION_POPUP_EDGE_PADDING },
    size: {
      padding: SUGGESTION_POPUP_EDGE_PADDING,
      apply: ({ availableHeight, elements }) => {
        const next = `min(${Math.max(0, availableHeight - SUGGESTION_POPUP_EDGE_PADDING)}px, ${SUGGESTION_POPUP_MAX_HEIGHT_CAP})`
        // `apply` runs on every transaction and on every (debounced) scroll. Skipping
        // identical writes avoids a per-keystroke style invalidation on the popover.
        if (elements.floating.style.maxHeight !== next) {
          elements.floating.style.maxHeight = next
        }
      },
    },
  }
}

function findScrollableAncestor(element: HTMLElement): HTMLElement | null {
  let current: HTMLElement | null = element
  while (current !== null) {
    const overflowY = window.getComputedStyle(current).overflowY
    if ((overflowY === 'auto' || overflowY === 'scroll') && current.scrollHeight > current.clientHeight) {
      return current
    }
    current = current.parentElement
  }
  return null
}

@Component({
  selector: 'app-tiptap-editor',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatChipsModule,
    MatIconModule,
    MatMenuModule,
    ProgressLoaderComponent,
    MinutesSkeletonComponent,
    MatProgressSpinnerModule,
    MatTooltipModule,
    TranslatePipe,
    TiptapEditorDirective,
    TiptapBubbleMenuDirective,
    AgendaMissingWarningComponent,
    SuggestionPopupComponent,
    CommentPopupComponent,
    FindReplacePanelComponent,
    TiptapEditorSidebarComponent,
    TiptapRevisionHistoryComponent,
  ],
  templateUrl: './tiptap-editor.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './tiptap-editor.component.scss',
})
export class TiptapEditorComponent implements OnInit, OnChanges, OnDestroy {
  @Input({ required: true })
  call!: Call

  @Input({ required: true })
  canUserEdit!: boolean

  /** A reviewer: the editor accepts input, but every change is forced through track changes. */
  @Input()
  canUserSuggest = false

  @Input()
  canUserComment = false

  /** Resolve a thread started by someone else. Own threads stay resolvable without it. */
  @Input()
  canResolveAnyThread = false

  /**
   * The entity whose minutes this editor holds, or null for the call's own. Fixed for the editor's lifetime: the
   * document it syncs is chosen once, so switching to another entity's minutes means a new editor.
   */
  @Input()
  callEntityId: number | null = null

  /** The citations of the entity's minutes; the call's own come with the call. */
  @Input()
  entityCitations: CitationMinuteNodes | null = null

  @Input()
  canDownloadRevisions = false

  @Output()
  openDocument = new EventEmitter<OpenDocumentEvent>()

  @Output()
  openMeeting = new EventEmitter<OpenMeetingEvent>()

  @Output()
  openAgendaUpload = new EventEmitter<void>()

  @Output()
  downloadRevision = new EventEmitter<RevisionDownloadEvent>()

  usersService = inject(UsersService)
  private http = inject(HttpClient)
  private dialog = inject(MatDialog)
  private toastService = inject(ToastService)
  private metricsService = inject(MetricsService)
  private translateService = inject(TranslateService)
  private editorSettingsService = inject(EditorSettingsService)
  private logger = inject(LoggerService)
  private changeDetectorRef = inject(ChangeDetectorRef)
  private route = inject(ActivatedRoute)
  private router = inject(Router)
  private queryClient = injectQueryClient()
  private destroyRef = inject(DestroyRef)
  private editorThreadsService = inject(EditorThreadsService)
  private editorRevisionsService = inject(EditorRevisionsService)

  document: Y.Doc | null = null

  hocuspocus: HocuspocusProvider | null = null

  editor: Editor | null = null
  findReplaceTarget: MultiEditorFindReplaceTarget | null = null
  hasInitiallySynced = false
  showWordCount$ = this.editorSettingsService.showWordCount$
  isEditorMaximized$ = this.editorSettingsService.isEditorMaximized$
  zoomLevel$ = this.editorSettingsService.zoomLevel$

  // The bubble menu is only rendered after the document has finished syncing, so this
  // ViewChild resolves later than `ngAfterViewInit` would fire. Using a setter lets us
  // attach the scroll-driven hide handler the first time Angular gives us a real ref.
  @ViewChild(TiptapBubbleMenuDirective, { read: ElementRef })
  set bubbleMenuElementRef(ref: ElementRef<HTMLElement> | undefined) {
    const element = ref?.nativeElement ?? null
    if (element === null || this.editor === null || this.bubbleMenuScrollHandler !== null) {
      return
    }
    this.attachBubbleMenuScrollHandler(this.editor, element)
  }

  @ViewChild(FindReplacePanelComponent)
  findReplacePanel?: FindReplacePanelComponent

  aiActionsConfig = AI_ACTION_CONFIGS
  readonly textColorOptions = TEXT_COLOR_OPTIONS
  currentTextColor = TEXT_COLOR_OPTIONS[0].value
  bubbleMenuOptions: { strategy: 'fixed' | 'absolute'; scrollTarget?: HTMLElement } = { strategy: 'absolute' }
  suggestionPopupOptions: SuggestionPopupOptions = buildSuggestionPopupOptions()

  trackChangesEnabled$ = new BehaviorSubject<boolean>(false)
  trackChangesToggleLabel$ = this.trackChangesEnabled$.pipe(
    map((enabled) => this.translateService.instant(enabled ? 'EDITOR.TRACK_CHANGES.DISABLE_TOOLTIP' : 'EDITOR.TRACK_CHANGES.ENABLE_TOOLTIP'))
  )
  activeSuggestion$ = new BehaviorSubject<ActiveSuggestion | null>(null)
  activeSuggestionId$ = this.activeSuggestion$.pipe(map((suggestion) => suggestion?.suggestionId ?? null))
  suggestionCount$ = new BehaviorSubject<number>(0)
  suggestions$ = new BehaviorSubject<SuggestionSummary[]>([])
  commentAnchors$ = new BehaviorSubject<CommentAnchorSummary[]>([])
  activeCommentAnchorId$ = new BehaviorSubject<string | null>(null)
  activeCommentDraftId$ = new BehaviorSubject<string | null>(null)
  sidebarFilter$ = new BehaviorSubject<SidebarFilter>('all')
  threads$ = this.editorThreadsService.threads$
  hasSidebarContent$ = combineLatest([this.suggestions$, this.threads$, this.commentAnchors$]).pipe(
    map(([suggestions, threads, commentAnchors]) => suggestions.length > 0 || threads.size > 0 || commentAnchors.length > 0)
  )
  hasComments$ = this.commentAnchors$.pipe(map((anchors) => anchors.length > 0))
  showTrackChangesToolbar$ = combineLatest([this.trackChangesEnabled$, this.suggestionCount$, this.hasComments$]).pipe(
    map(([enabled, suggestionCount, hasComments]) => enabled || suggestionCount > 0 || hasComments)
  )
  navigationTargetCount$ = combineLatest([this.suggestionCount$, this.commentAnchors$, this.sidebarFilter$]).pipe(
    map(([suggestionCount, comments, filter]) => {
      const includes = navigationIncludes(filter)
      return (includes.changes ? suggestionCount : 0) + (includes.comments ? comments.length : 0)
    })
  )
  findReplaceOpen$ = new BehaviorSubject<boolean>(false)
  activeCommentPopup$ = new BehaviorSubject<string | null>(null)
  activeCommentPopupThread$: Observable<EditorThread | null> = combineLatest([this.activeCommentPopup$, this.threads$]).pipe(
    map(([anchorId, threads]) => (anchorId === null ? null : (threads.get(anchorKey('TEXT_RANGE', anchorId)) ?? null)))
  )

  revisionHistoryOpen$ = new BehaviorSubject<boolean>(false)
  revisions$ = new BehaviorSubject<RevisionSummary[]>([])
  revisionsLoading$ = new BehaviorSubject<boolean>(false)
  filterSystemOnly$ = new BehaviorSubject<boolean>(false)
  selectedRevisionId$ = new BehaviorSubject<number | null>(null)
  // Non-null only while previewing a past revision — the most recent revision is "current", not a preview.
  viewedRevision$: Observable<RevisionSummary | null> = combineLatest([this.selectedRevisionId$, this.revisions$]).pipe(
    map(([selectedId, revisions]) => {
      if (selectedId === null) {
        return null
      }
      const revision = revisions.find((candidate) => candidate.id === selectedId) ?? null
      if (revision === null || revision.isCurrent) {
        return null
      }
      return revision
    })
  )
  previewEditor: Editor | null = null
  private revisionRequestSeq = 0
  private revisionListRequestSeq = 0
  // Citations snapshotted with the revision being previewed / restored, used instead of the live call's citations.
  private previewCitations: CitationMinuteNodes | null = null
  // null = no restore override, so the live call's citations apply.
  private restoredCitations: CitationMinuteNodes | null = null

  readonly suggestionPopupPluginKey = new PluginKey('suggestionPopup')
  readonly commentPopupPluginKey = new PluginKey('commentPopup')

  private citationsSubscription: Subscription | null = null
  /** The nested editors of the restricted items' scope documents, which carry citations of their own. */
  private readonly scopeEditors = new Map<Editor, () => number | undefined>()
  private templateItemSubscription: Subscription | null = null
  private synced$ = new BehaviorSubject<boolean>(false)
  private bubbleMenuScrollHandler: (() => void) | null = null
  private bubbleMenuScrollDebounceTimer: number | null = null
  private bubbleMenuScrollElement: HTMLElement | null = null
  private cachedScrollElement: HTMLElement | null = null
  private suggestionRepositionRafId: number | null = null
  private commentRepositionRafId: number | null = null
  private openCommentsRafIds: [number | null, number | null] = [null, null]

  shouldShowFormattingMenu = (props: { element: HTMLElement; view: EditorView; state: EditorState; from: number; to: number }): boolean => {
    if (this.editor === null || (!this.canUserEdit && !this.canUserComment)) {
      return false
    }
    const { doc, selection } = props.state
    if (selection.empty) {
      return false
    }
    const isEmptyTextBlock = isTextSelection(selection) && doc.textBetween(props.from, props.to).length === 0
    if (isEmptyTextBlock) {
      return false
    }
    // Neither an AI action nor a comment is worth offering on content a regeneration is about to replace.
    if (overlapsLockedRange(props.state, props.from, props.to)) {
      return false
    }
    // A commenter's editor is `contenteditable=false`, so `view.hasFocus()` is never
    // true — the DOM element isn't focusable. Fall back to checking the browser
    // selection lives inside the editor so the menu still appears on a plain selection.
    const isChildOfMenu = props.element.contains(document.activeElement)
    return isChildOfMenu || props.view.hasFocus() || this.selectionIsInsideEditor(props.view)
  }

  private selectionIsInsideEditor(view: EditorView): boolean {
    const domSelection = view.dom.ownerDocument.getSelection()
    if (domSelection === null || domSelection.rangeCount === 0) {
      return false
    }
    const { anchorNode, focusNode } = domSelection
    return anchorNode !== null && focusNode !== null && view.dom.contains(anchorNode) && view.dom.contains(focusNode)
  }

  shouldShowSuggestionPopup = (props: { state: EditorState; oldState?: EditorState; from: number; to: number }): boolean => {
    if (this.editor === null) {
      this.refreshActiveSuggestion(null)
      return false
    }

    if (props.from !== props.to) {
      this.refreshActiveSuggestion(null)
      return false
    }

    // Suppress the popover while the document is changing — e.g. the user is
    // typing a tracked change. The popover should only appear when the cursor
    // is manually navigated onto an existing suggestion.
    const oldState = props.oldState ?? null
    if (oldState !== null && !oldState.doc.eq(props.state.doc)) {
      this.refreshActiveSuggestion(null)
      return false
    }

    const suggestion = findSuggestionAtPosition(props.state, props.from)
    if (suggestion === null) {
      this.refreshActiveSuggestion(null)
      return false
    }

    const fallbackAuthor = this.translateService.instant('EDITOR.TRACK_CHANGES.UNKNOWN_AUTHOR')
    const collected = collectSuggestionContent(props.state, suggestion.suggestionId)

    this.refreshActiveSuggestion({
      suggestionId: suggestion.suggestionId,
      authorName: suggestion.authorName ?? fallbackAuthor,
      createdAt: suggestion.createdAt,
      insertionRuns: collected.insertionRuns,
      deletionRuns: collected.deletionRuns,
      formattingAdded: collected.formattingAdded,
      formattingRemoved: collected.formattingRemoved,
      listChanges: collected.listChanges,
      colorChange: collected.colorChange,
    })
    // In maximized mode the sidebar replaces the popover, but the active-suggestion
    // state still drives the scroll-and-highlight effect on the matching card.
    if (this.editorSettingsService.isEditorMaximized) {
      return false
    }
    return true
  }

  shouldShowCommentPopup = (props: { state: EditorState; oldState?: EditorState; from: number; to: number }): boolean => {
    const anchorId = this.findCommentAnchorAtCursor(props)
    this.refreshActiveCommentAnchor(anchorId)

    if (this.editor === null || !this.canUserComment || this.editorSettingsService.isEditorMaximized) {
      this.activeCommentPopup$.next(null)
      return false
    }
    if (anchorId === null) {
      this.activeCommentPopup$.next(null)
      return false
    }
    if (this.activeCommentPopup$.value !== anchorId) {
      this.activeCommentPopup$.next(anchorId)
    }
    return true
  }

  ngOnInit() {
    const user = this.usersService.currentUser$.value
    const caretUser = buildCaretUser(user)

    this.trackChangesEnabled$.next(this.isTrackChangesForced ? true : readTrackChangesEnabled(this.call.id))

    this.document = new Y.Doc()

    // Minutes holding restricted items run over polling so the base and every scope document share the one transport;
    // forced this way it must not opt the user into the permanent polling-fallback table. Entity minutes are never
    // partitioned, so they keep SSE.
    const hasRestrictedItems = this.callEntityId === null && this.call.hasRestrictedMinuteItems
    const forcePolling = user?.tiptapEditorPollingFallbackActivatedAt != null || hasRestrictedItems

    this.hocuspocus = new SseSyncProvider({
      name: this.callEntityId === null ? `minutes:${this.call.id}` : `minutes:${this.call.id}:entity:${this.callEntityId}`,
      document: this.document,
      forcePolling,
      suppressFallbackReport: hasRestrictedItems,
      onSynced: ({ state }) => {
        if (!this.hasInitiallySynced && state) {
          this.hasInitiallySynced = true
          this.synced$.next(true)
          this.changeDetectorRef.detectChanges()
        }
      },
      onStatelessPayload: (payload) => {
        if (payload === STATELESS_REFETCH_CALL_MESSAGE_TYPE) {
          this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(this.call.id) })
        } else if (payload === STATELESS_REVISION_UPDATE_MESSAGE_TYPE) {
          this.refreshRevisionsIfOpen()
        } else if (payload.startsWith(STATELESS_THREAD_UPDATE_PREFIX)) {
          this.editorThreadsService.requestRefetch(this.call.id)
        }
      },
    })

    this.editorThreadsService.loadAll(this.call.id)

    const extensions: AnyExtension[] = [
      // Swap the plain restricted-section node for the one that mounts a nested editor per restricted item's scope.
      ...EDITOR_STRUCTURAL_EXTENSIONS.filter((extension) => extension.name !== 'restricted_section'),
      RestrictedSectionNodeView.configure({
        callId: this.call.id,
        caretUser,
        editable: this.canUserEdit || this.canUserSuggest,
        forcePolling,
        buildExtensions: () => [
          this.buildCitationExtension(() => this.restoredCitations ?? this.liveCitations),
          FindAndReplace.configure({ injectCSS: false }),
        ],
        scopeEditors: this.scopeEditors,
        onScopeEditorsChanged: () => this.findReplaceTarget?.refresh(),
        lockedLabel: this.translateService.instant('EDITOR.RESTRICTED_SECTION_LOCKED'),
      }),
      ProtectRestrictedMounts,
      CharacterCount,
      Collaboration.configure({
        document: this.document,
      }),
      UniqueUserCollaborationCaret.configure({ provider: this.hocuspocus, user: caretUser }),
      TrackChangesExtension.configure(this.trackChangesCommandOptions()),
      FindAndReplace.configure({ injectCSS: false }),
    ]

    // Installed unconditionally: citations can arrive on a later call refresh, and only an
    // installed plugin can turn them into decorations when `syncCitations` asks for a rebuild.
    // Read lazily, so a call refresh that carries no citations reaches the plugin as null. A restored revision's
    // snapshotted citations take precedence over the live call's until the next generation refreshes them.
    extensions.push(this.buildCitationExtension(() => this.restoredCitations ?? this.liveCitations))

    // Also installed unconditionally: a regeneration can start while the editor is already open, and the locked
    // set is read lazily so the plugin sees each call refresh.
    extensions.push(LockedItemsExtension.configure({ getLockedTemplateItemIds: () => this.regeneratingTemplateItemIds }))

    extensions.push(MinuteTreeExtension)
    extensions.push(AttendeeStructureExtension)
    extensions.push(ExportScopeExtension)
    extensions.push(InsertionPulseExtension)

    this.editor = new Editor({
      extensions,
      editorProps: {
        handleScrollToSelection: (view) => this.smoothScrollToSelection(view),
      },
    })
    this.findReplaceTarget = this.buildFindReplaceTarget(this.editor)
    this.editor.setEditable(this.canUserEdit || this.canUserSuggest)
    if (!this.canUserComment) {
      this.editor.view.dom.classList.add('comments-hidden')
    }
    const getScrollElement = () => this.resolveScrollElement()
    this.bubbleMenuOptions = {
      strategy: 'absolute',
      get scrollTarget() {
        return getScrollElement()
      },
    }
    this.suggestionPopupOptions = buildSuggestionPopupOptions(getScrollElement)

    this.editor.on('update', () => {
      this.refreshSuggestionCount()
      this.refreshSuggestions()
      this.refreshCommentAnchors()
    })
    this.refreshCommentAnchors()
    this.trackChangesEnabled$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.refreshSuggestionCount()
    })
    this.isEditorMaximized$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.refreshSuggestions()
      this.refreshCommentAnchors()
    })

    // The bubble menu computes its position before Angular renders the popover's
    // content, so the element measures at zero width and `shift` sees no overflow.
    // Re-trigger an update once the popover content has been laid out.
    this.activeSuggestion$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((suggestion) => {
      if (suggestion === null) {
        return
      }
      this.suggestionRepositionRafId = this.repositionPopupAfterLayout(this.suggestionPopupPluginKey, this.suggestionRepositionRafId)
    })
    this.activeCommentPopup$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((anchorId) => {
      if (anchorId === null) {
        return
      }
      this.commentRepositionRafId = this.repositionPopupAfterLayout(this.commentPopupPluginKey, this.commentRepositionRafId)
    })

    // Also unconditional, so citations that arrive later still honour the show-citations setting — on both the live
    // editor and the revision preview when it is open.
    this.citationsSubscription = this.editorSettingsService.showCitations$.subscribe((showCitations) => {
      this.applyCitationsVisibility(this.editor, showCitations)
      this.applyCitationsVisibility(this.previewEditor, showCitations)
    })

    this.templateItemSubscription = this.route.queryParams
      .pipe(
        map((params) => params['templateItem'] ?? null),
        filter((templateItemId) => templateItemId !== null),
        switchMap((templateItemId) =>
          this.synced$.pipe(
            filter((synced) => synced),
            take(1),
            map(() => templateItemId)
          )
        )
      )
      .subscribe((templateItemId) => {
        this.scrollToTemplateItem(Number(templateItemId))
        this.router.navigate([], {
          queryParams: { templateItem: null },
          queryParamsHandling: 'merge',
          replaceUrl: true,
        })
      })
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.editor === null) {
      return
    }
    this.syncEditableState(changes)
    this.syncCommentState(changes)
    this.syncCitations(changes)
    this.syncLockedItems(changes)
  }

  private syncLockedItems(changes: SimpleChanges) {
    const change = changes['call']
    if (this.editor === null || change === undefined || change.firstChange) {
      return
    }
    // Every refetch brings a fresh array, so the ids are what says whether the lock set moved.
    const previousCall = change.previousValue as Call
    const previouslyRegeneratingTemplateItemIds = previousCall.minutesGenerationTargetTemplateItemIds ?? []
    if (haveSameLockedItems(previouslyRegeneratingTemplateItemIds, this.regeneratingTemplateItemIds)) {
      return
    }
    refreshLockedItems(this.editor)
    this.scrollToFirstRegeneratingItem(previouslyRegeneratingTemplateItemIds)
  }

  /**
   * Follows a starting regeneration to the item it has just taken over, so the user watches the part of the
   * minutes that is being rewritten rather than wherever they happened to be scrolled to.
   *
   * Only items the lock set has gained count as starting: one that was already being rewritten before this
   * change should not pull the viewport back to itself.
   */
  private scrollToFirstRegeneratingItem(previouslyRegeneratingTemplateItemIds: number[]) {
    if (this.editor === null) {
      return
    }

    const alreadyRegeneratingTemplateItemIds = new Set(previouslyRegeneratingTemplateItemIds)
    const startedTemplateItemIds = this.regeneratingTemplateItemIds.filter((templateItemId) => !alreadyRegeneratingTemplateItemIds.has(templateItemId))
    const [firstStartedRange] = findLockedRanges(this.editor.state.doc, startedTemplateItemIds)
    if (firstStartedRange === undefined) {
      return
    }

    const templateItemId = this.editor.state.doc.nodeAt(firstStartedRange.from)?.attrs['templateItemId']
    if (typeof templateItemId !== 'string') {
      return
    }

    this.scrollToTemplateItem(Number(templateItemId))
  }

  private syncEditableState(changes: SimpleChanges) {
    const change = changes['canUserEdit'] ?? changes['canUserSuggest']
    if (this.editor === null || change === undefined || change.firstChange) {
      return
    }
    this.editor.setEditable(this.canUserEdit || this.canUserSuggest)
    if (this.isTrackChangesForced) {
      this.trackChangesEnabled$.next(true)
    }
  }

  private syncCommentState(changes: SimpleChanges) {
    const change = changes['canUserComment']
    if (this.editor === null || change === undefined || change.firstChange) {
      return
    }
    this.editor.view.dom.classList.toggle('comments-hidden', !this.canUserComment)
    if (!this.canUserComment) {
      this.activeCommentDraftId$.next(null)
      this.activeCommentPopup$.next(null)
    }
    this.refreshCommentAnchors()
  }

  private get liveCitations(): CitationMinuteNodes | null {
    return this.callEntityId === null ? this.call.minutesCitations : this.entityCitations
  }

  private syncCitations(changes: SimpleChanges) {
    if (this.editor === null) {
      return
    }
    const hasCitationsChanged =
      this.callEntityId === null ? this.hasCallCitationsChanged(changes['call']) : this.hasEntityCitationsChanged(changes['entityCitations'])
    if (hasCitationsChanged) {
      // A genuine citation refresh (e.g. a regeneration) supersedes any restored snapshot, so fall back to the live call.
      this.restoredCitations = null
      this.rebuildCitations()
    }
  }

  private hasCallCitationsChanged(change: SimpleChange | undefined): boolean {
    return change !== undefined && !change.firstChange && (change.previousValue as Call).minutesCitations !== this.call.minutesCitations
  }

  private hasEntityCitationsChanged(change: SimpleChange | undefined): boolean {
    return change !== undefined && !change.firstChange
  }

  @HostListener('document:keydown', ['$event'])
  handleZoomShortcut(event: KeyboardEvent) {
    if (!(event.metaKey || event.ctrlKey) || event.altKey) {
      return
    }
    if (!this.editorSettingsService.isEditorMaximized || this.editor === null || !this.editor.view.hasFocus()) {
      return
    }
    switch (event.key) {
      case '=':
      case '+':
        event.preventDefault()
        this.editorSettingsService.zoomIn()
        break
      case '-':
      case '_':
        event.preventDefault()
        this.editorSettingsService.zoomOut()
        break
      case '0':
        event.preventDefault()
        this.editorSettingsService.resetZoom()
        break
    }
  }

  @HostListener('document:keydown', ['$event'])
  handleFindReplaceShortcut(event: KeyboardEvent) {
    if (!(event.metaKey || event.ctrlKey) || event.altKey || event.shiftKey || event.key.toLowerCase() !== 'f') {
      return
    }
    if (this.editor === null || (!this.editor.view.hasFocus() && !this.findReplaceOpen$.value)) {
      return
    }
    event.preventDefault()
    if (this.findReplaceOpen$.value) {
      this.findReplacePanel?.focusSearchInput()
      return
    }
    this.findReplaceOpen$.next(true)
  }

  private attachBubbleMenuScrollHandler(editor: Editor, bubbleMenuElement: HTMLElement) {
    this.bubbleMenuScrollHandler = () => {
      if (!editor.state.selection.empty) {
        return
      }

      if (this.bubbleMenuScrollDebounceTimer !== null) {
        clearTimeout(this.bubbleMenuScrollDebounceTimer)
      }

      // Must exceed the bubble menu plugin's internal resizeDelay (60ms) so our
      // visibility correction runs after the plugin's scroll-triggered updatePosition().
      this.bubbleMenuScrollDebounceTimer = window.setTimeout(() => {
        if (editor.state.selection.empty) {
          bubbleMenuElement.style.visibility = 'hidden'
        }
      }, 70)
    }

    this.bubbleMenuScrollElement = this.resolveScrollElement() ?? (editor.view.dom as HTMLElement)
    this.bubbleMenuScrollElement.addEventListener('scroll', this.bubbleMenuScrollHandler, { passive: true })
  }

  private resolveScrollElement(): HTMLElement | undefined {
    if (this.cachedScrollElement?.isConnected) {
      return this.cachedScrollElement
    }
    const dom = this.editor?.view.dom as HTMLElement | undefined
    this.cachedScrollElement = (dom?.closest('.editor') as HTMLElement | null) ?? null
    return this.cachedScrollElement ?? dom
  }

  private repositionPopupAfterLayout(pluginKey: PluginKey, previousRafId: number | null): number {
    if (previousRafId !== null) {
      cancelAnimationFrame(previousRafId)
    }
    return requestAnimationFrame(() => {
      if (this.editor === null) {
        return
      }
      this.editor.view.dispatch(this.editor.state.tr.setMeta(pluginKey, 'updatePosition'))
    })
  }

  ngOnDestroy() {
    if (this.bubbleMenuScrollDebounceTimer !== null) {
      clearTimeout(this.bubbleMenuScrollDebounceTimer)
    }
    if (this.suggestionRepositionRafId !== null) {
      cancelAnimationFrame(this.suggestionRepositionRafId)
      this.suggestionRepositionRafId = null
    }
    if (this.commentRepositionRafId !== null) {
      cancelAnimationFrame(this.commentRepositionRafId)
      this.commentRepositionRafId = null
    }
    this.cancelOpenCommentsRafs()
    if (this.bubbleMenuScrollHandler !== null && this.bubbleMenuScrollElement !== null) {
      this.bubbleMenuScrollElement.removeEventListener('scroll', this.bubbleMenuScrollHandler)
    }
    this.citationsSubscription?.unsubscribe()
    this.templateItemSubscription?.unsubscribe()
    this.synced$.complete()
    this.findReplaceOpen$.complete()
    this.trackChangesEnabled$.complete()
    this.activeSuggestion$.complete()
    this.suggestionCount$.complete()
    this.suggestions$.complete()
    this.commentAnchors$.complete()
    this.activeCommentAnchorId$.complete()
    this.activeCommentDraftId$.complete()
    this.sidebarFilter$.complete()
    this.activeCommentPopup$.complete()
    this.revisionHistoryOpen$.complete()
    this.revisions$.complete()
    this.revisionsLoading$.complete()
    this.filterSystemOnly$.complete()
    this.selectedRevisionId$.complete()
    this.destroyRevisionPreview()
    this.editor?.destroy()
    this.hocuspocus?.destroy()
    this.document?.destroy()
    this.editorThreadsService.reset()
  }

  toggleFindReplace() {
    this.findReplaceOpen$.next(!this.findReplaceOpen$.value)
  }

  closeFindReplace() {
    this.findReplaceOpen$.next(false)
  }

  toggleRevisionHistory() {
    if (this.revisionHistoryOpen$.value) {
      this.closeRevisionHistory()
      return
    }

    this.revisionHistoryOpen$.next(true)
    this.editorSettingsService.setIsEditorMaximized(true)
    this.closeFindReplace()
    this.metricsService.sendMetric(this.call.id, MetricType.TOGGLE_REVISION_HISTORY)
    void this.loadRevisions()
  }

  private closeRevisionHistory() {
    if (!this.revisionHistoryOpen$.value) {
      return
    }
    // Invalidate any in-flight list load so its response can't land after the panel has closed.
    this.revisionListRequestSeq++
    this.revisionsLoading$.next(false)
    this.revisionHistoryOpen$.next(false)
    this.backToCurrent()
  }

  setFilterSystemOnly(filterSystemOnly: boolean) {
    this.filterSystemOnly$.next(filterSystemOnly)
  }

  async selectRevision(revisionId: number) {
    const revision = this.revisions$.value.find((candidate) => candidate.id === revisionId) ?? null
    if (revision === null || revision.isCurrent) {
      this.backToCurrent()
      return
    }

    // Guard against out-of-order responses: a slower fetch for an earlier click must not overwrite a later selection.
    const requestSeq = ++this.revisionRequestSeq
    try {
      const { content, citations } = await this.editorRevisionsService.getRevision(this.call.id, revisionId, this.callEntityId)
      if (requestSeq !== this.revisionRequestSeq) {
        return
      }
      this.showRevisionPreview(content, citations)
      this.selectedRevisionId$.next(revisionId)
    } catch (error) {
      // A superseded request's failure belongs to a selection the user has already moved on from — stay quiet.
      if (requestSeq !== this.revisionRequestSeq) {
        return
      }
      this.toastService.show('EDITOR.REVISION_HISTORY.LOAD_FAILED')
      this.logger.error('Failed to load revision content', error)
    }
  }

  requestRevisionDownload({ revisionId, format }: { revisionId: number; format: 'pdf' | 'docx' }) {
    const revision = this.revisions$.value.find((candidate) => candidate.id === revisionId) ?? null
    if (revision !== null) {
      this.downloadRevision.emit({ revision, format })
    }
  }

  backToCurrent() {
    this.revisionRequestSeq++
    this.selectedRevisionId$.next(null)
    this.destroyRevisionPreview()
  }

  async restoreSelectedRevision() {
    const revisionId = this.selectedRevisionId$.value
    if (revisionId === null || this.editor === null) {
      return
    }

    try {
      const { content, citations } = await this.editorRevisionsService.restoreRevision(this.call.id, revisionId, this.callEntityId)
      if (!replaceWithAuthoritativeContent(this.editor, content)) {
        throw new Error('The editor refused the restored revision content')
      }
      // The restored content carries the citations snapshotted with that revision, not the live call's, so surface
      // them until the next generation refreshes minutesCitations (see syncCitations).
      this.restoredCitations = citations
      this.rebuildCitations()
      this.metricsService.sendMetric(this.call.id, MetricType.RESTORE_REVISION)
      this.toastService.show('EDITOR.REVISION_HISTORY.RESTORE_SUCCESS')
      this.backToCurrent()
      await this.loadRevisions()
    } catch (error) {
      this.toastService.show('EDITOR.REVISION_HISTORY.RESTORE_FAILED')
      this.logger.error('Failed to restore revision', error)
    }
  }

  private async loadRevisions() {
    // Overlapping loads (panel open + SSE revision-update, say) must not apply out of order, nor apply to a
    // different call if the input changed while a fetch was in flight.
    const requestSeq = ++this.revisionListRequestSeq
    const callId = this.call.id
    this.revisionsLoading$.next(true)
    try {
      const revisions = await this.editorRevisionsService.listRevisions(callId, this.callEntityId)
      if (this.isCurrentRevisionListRequest(requestSeq, callId)) {
        this.revisions$.next(revisions)
      }
    } catch (error) {
      if (this.isCurrentRevisionListRequest(requestSeq, callId)) {
        this.toastService.show('EDITOR.REVISION_HISTORY.LOAD_FAILED')
        this.logger.error('Failed to load revision history', error)
      }
    } finally {
      if (this.isCurrentRevisionListRequest(requestSeq, callId)) {
        this.revisionsLoading$.next(false)
      }
    }
  }

  private isCurrentRevisionListRequest(requestSeq: number, callId: number): boolean {
    return requestSeq === this.revisionListRequestSeq && callId === this.call.id
  }

  // A revision-update arrived over SSE (another client generated or restored a revision); refresh the list if the
  // panel is showing. When closed it reloads on next open, so there is nothing to do.
  private refreshRevisionsIfOpen() {
    if (this.revisionHistoryOpen$.value) {
      void this.loadRevisions()
    }
  }

  private rebuildCitations() {
    const editors = this.editor === null ? [...this.scopeEditors.keys()] : [this.editor, ...this.scopeEditors.keys()]
    editors.forEach((editor) => editor.view.dispatch(editor.state.tr.setMeta(citationPluginKey, { type: 'REBUILD' })))
  }

  private applyCitationsVisibility(editor: Editor | null, showCitations: boolean) {
    const dom = editor?.view.dom ?? null
    if (dom === null) {
      return
    }
    dom.classList.toggle('citations-hidden', !showCitations)
  }

  // `interactive` is false for the read-only revision preview, where clicking a citation must not jump to its
  // transcript line or source document.
  private buildCitationExtension(getCitations: () => CitationMinuteNodes | null, interactive = true) {
    return buildMinutesCitationExtension({
      translateService: this.translateService,
      getCitations,
      getResources: () => this.call.userUploadedResources.map((resource) => ({ id: resource.id, fileName: resource.fileName })),
      onOpenDocument: interactive
        ? (event) => {
            this.closeRevisionHistory()
            this.openDocument.emit(event)
          }
        : null,
      onOpenMeeting: interactive
        ? (event) => {
            this.closeRevisionHistory()
            this.handleOpenMeeting(event)
          }
        : null,
    })
  }

  private showRevisionPreview(content: JSONContent, citations: CitationMinuteNodes | null) {
    this.previewCitations = citations
    if (this.previewEditor === null) {
      this.previewEditor = new Editor({
        extensions: [...EDITOR_STRUCTURAL_EXTENSIONS, this.buildCitationExtension(() => this.previewCitations, false)],
        editable: false,
        content,
      })
      this.applyCitationsVisibility(this.previewEditor, this.editorSettingsService.showCitations)
    } else {
      this.previewEditor.commands.setContent(content)
    }
    this.previewEditor.view.dispatch(this.previewEditor.state.tr.setMeta(citationPluginKey, { type: 'REBUILD' }))
  }

  private destroyRevisionPreview() {
    this.previewEditor?.destroy()
    this.previewEditor = null
    this.previewCitations = null
  }

  /**
   * Searches the minutes in reading order: the editor's own results by position, and each restricted item's nested
   * editor where its mount sits. The editor replaces through track changes; a nested editor replaces directly.
   */
  private buildFindReplaceTarget(editor: Editor): MultiEditorFindReplaceTarget {
    const ownSegment: SearchSegment = {
      editor,
      anchorOf: (resultFrom) => resultFrom,
      replaceCurrent: (target) => runReplaceWithTracking(target, this.trackChangesCommandOptions()),
      replaceAll: (target) => runReplaceAllWithTracking(target, this.trackChangesCommandOptions()),
    }
    return new MultiEditorFindReplaceTarget(() => [
      ownSegment,
      ...Array.from(this.scopeEditors, ([scopeEditor, mountPosition]) => ({ editor: scopeEditor, anchorOf: () => mountPosition() ?? 0 })),
    ])
  }

  replaceCurrentMatch() {
    if (this.canUserEdit) {
      this.findReplaceTarget?.replaceCurrent()
    }
  }

  replaceAllMatches() {
    if (this.canUserEdit) {
      this.findReplaceTarget?.replaceAll()
    }
  }

  /** A reviewer may edit only through track changes, so the toggle is fixed on for them. */
  get isTrackChangesForced(): boolean {
    return this.canUserSuggest && !this.canUserEdit
  }

  toggleTrackChanges() {
    if (this.isTrackChangesForced) {
      return
    }
    const nextValue = !this.trackChangesEnabled$.value
    this.trackChangesEnabled$.next(nextValue)
    writeTrackChangesEnabled(this.call.id, nextValue)
  }

  acceptSuggestion(suggestionId: string) {
    if (!this.canUserEdit) {
      return
    }
    this.resolveSuggestion(suggestionId, 'accept')
  }

  rejectSuggestion(suggestionId: string) {
    if (!this.canUserEdit) {
      return
    }
    this.resolveSuggestion(suggestionId, 'reject')
  }

  /** Formatting and typing: allowed for a reviewer too, whose changes are tracked instead of direct. */
  get canModifyMinutes(): boolean {
    return this.canUserEdit || this.canUserSuggest
  }

  get currentUserId(): number | null {
    return this.usersService.currentUser$.value?.id ?? null
  }

  /**
   * Withdrawing one's own pending suggestion. It reverts the same way a rejection does, but is
   * offered to a reviewer, who may not act on anyone else's.
   *
   * The thread is archived before the document changes: the server authorizes a discard by
   * reading the suggestion's author off the live document, so the mark has to still be there
   * when the request lands. Reverting first would race the sync and lose a valid discard.
   */
  async discardSuggestion(suggestionId: string): Promise<void> {
    if (this.editor === null || this.canUserEdit || !this.canUserSuggest) {
      return
    }

    const currentUserId = this.currentUserId
    const suggestion = collectAllSuggestions(this.editor.state).find((candidate) => candidate.id === suggestionId) ?? null
    if (currentUserId === null || suggestion === null || suggestion.authorId !== currentUserId) {
      return
    }

    // Mirrors resolveSuggestion's own guard: a locked agenda item refuses the revert, and
    // archiving first would then leave the suggestion behind with its thread already gone.
    if (coversLockedRange(this.editor.state, findAllRangesForSuggestion(this.editor.state, suggestionId))) {
      return
    }

    try {
      await this.editorThreadsService.archiveThread(this.call.id, 'SUGGESTION', suggestionId, this.callEntityId)
    } catch (error) {
      this.logger.error('Failed to archive suggestion thread, leaving the suggestion in place', error)
      return
    }

    // A regeneration can lock the range while the archive is in flight, and the revert below
    // would then bail silently. The suggestion simply stays put: archiving is idempotent, so
    // discarding again once the lock lifts completes it.
    if (coversLockedRange(this.editor.state, findAllRangesForSuggestion(this.editor.state, suggestionId))) {
      this.logger.warn(`Suggestion ${suggestionId} was locked while its thread was archived, so it stays until discarded again.`)
      return
    }

    this.resolveSuggestion(suggestionId, 'reject', { archiveThread: false })
  }

  private cachedThreadCurrentUserSource: MeUser | null = null
  private cachedThreadCurrentUser: { name: string } = { name: 'Unknown User' }

  get threadCurrentUser(): { name: string } {
    const user = this.usersService.currentUser$.value
    if (this.cachedThreadCurrentUserSource !== user) {
      this.cachedThreadCurrentUserSource = user
      this.cachedThreadCurrentUser = { name: user !== null ? constructNameForUser(user) : 'Unknown User' }
    }
    return this.cachedThreadCurrentUser
  }

  get canPostComment(): boolean {
    return this.canUserComment && this.usersService.currentUser$.value !== null
  }

  get displayState(): EditorDisplayState {
    // An entity's minutes are only ever opened once they exist, and no run of the call's own minutes rewrites them.
    if (this.callEntityId !== null) {
      return this.editor !== null && this.hasInitiallySynced ? 'EDITOR' : 'SYNCING'
    }
    return deriveEditorDisplayState({
      isPostProcessingComplete: this.call.isPostProcessingComplete,
      isMinutesGenerationOngoing: this.call.isMinutesGenerationOngoing,
      isMinutesGenerationTargeted: isMinutesGenerationTargeted(this.call),
      minutesGenerationProgress: this.call.minutesGenerationProgress,
      hasEditorDocument: this.call.hasEditorDocument,
      isSpeechMissing: this.call.isSpeechMissing,
      editorConstructed: this.editor !== null,
      hasInitiallySynced: this.hasInitiallySynced,
    })
  }

  /** The agenda items a regeneration is currently rewriting, which are locked while it runs. */
  get regeneratingTemplateItemIds(): number[] {
    if (this.callEntityId !== null) {
      return []
    }
    return this.call.minutesGenerationTargetTemplateItemIds ?? []
  }

  onCardSubmitComment(suggestionId: string, body: string) {
    this.postThreadComment('SUGGESTION', suggestionId, body)
  }

  private postThreadComment(anchorType: EditorThreadAnchorType, anchorId: string, body: string, onSuccess?: () => void) {
    const user = this.usersService.currentUser$.value
    if (user === null) {
      return
    }
    this.editorThreadsService
      .postComment({
        callId: this.call.id,
        anchorType,
        anchorId,
        body,
        currentUser: { id: user.id, name: constructNameForUser(user) },
      })
      .then(() => onSuccess?.())
      .catch((error: unknown) => {
        const message = error instanceof Error ? error.message : 'unknown error'
        this.logger.warn(`Failed to post ${anchorType} comment: ${message}`)
      })
  }

  addComment() {
    if (this.editor === null || !this.canUserComment) {
      return
    }
    const { from, to } = this.editor.state.selection
    if (from === to) {
      return
    }
    const commentAnchorType = this.editor.state.schema.marks[COMMENT_ANCHOR_MARK_NAME] ?? null
    if (commentAnchorType === null) {
      return
    }
    const anchorId = crypto.randomUUID()
    this.editor.chain().setMark(COMMENT_ANCHOR_MARK_NAME, { anchorId }).setTextSelection(to).run()
    this.activeCommentDraftId$.next(anchorId)
    this.refreshCommentAnchors()
    this.editorSettingsService.setIsEditorMaximized(true)
    this.sidebarFilter$.next('comments')
  }

  onCommentSubmit(anchorId: string, body: string) {
    if (!this.canUserComment) {
      return
    }
    this.postThreadComment('TEXT_RANGE', anchorId, body, () => {
      if (this.activeCommentDraftId$.value === anchorId) {
        this.activeCommentDraftId$.next(null)
      }
    })
  }

  resolveCommentThread(anchorId: string) {
    if (!this.canUserComment || this.isCommentAnchorLocked(anchorId)) {
      return
    }
    const thread = this.editorThreadsService.threadFor('TEXT_RANGE', anchorId)
    if (!canResolveThread(thread, this.currentUserId, this.canResolveAnyThread)) {
      return
    }
    this.editorThreadsService
      .archiveThread(this.call.id, 'TEXT_RANGE', anchorId)
      .then(() => {
        this.removeCommentAnchorMark(anchorId)
        this.refreshCommentAnchors()
        if (this.activeCommentDraftId$.value === anchorId) {
          this.activeCommentDraftId$.next(null)
        }
        this.activeCommentPopup$.next(null)
      })
      .catch((error) => {
        this.logger.error('Failed to archive comment thread', error)
      })
  }

  discardCommentDraft(anchorId: string) {
    this.removeCommentAnchorMark(anchorId)
    this.refreshCommentAnchors()
    if (this.activeCommentDraftId$.value === anchorId) {
      this.activeCommentDraftId$.next(null)
    }
    this.activeCommentPopup$.next(null)
  }

  /**
   * Whether the anchor sits in an agenda item a regeneration is rewriting.
   *
   * Its mark cannot be removed while the item is locked, so archiving the thread would leave the document
   * anchoring a comment nobody can reach.
   */
  private isCommentAnchorLocked(anchorId: string): boolean {
    if (this.editor === null) {
      return false
    }
    const range = collectCommentAnchors(this.editor.state).get(anchorId) ?? null
    return range !== null && overlapsLockedRange(this.editor.state, range.from, range.to)
  }

  private removeCommentAnchorMark(anchorId: string) {
    if (this.editor === null) {
      return
    }
    const markType = this.editor.state.schema.marks[COMMENT_ANCHOR_MARK_NAME] ?? null
    if (markType === null) {
      return
    }
    const range = collectCommentAnchors(this.editor.state).get(anchorId) ?? null
    if (range === null) {
      return
    }
    const transaction = this.editor.state.tr
    transaction.removeMark(range.from, range.to, markType.create({ anchorId }))
    transaction.setMeta(trackChangesPluginKey, TRACK_CHANGES_SKIP_META)
    this.editor.view.dispatch(transaction)
  }

  viewCommentAnchor(anchorId: string) {
    if (this.editor === null) {
      return
    }
    const range = collectCommentAnchors(this.editor.state).get(anchorId) ?? null
    if (range === null) {
      return
    }
    this.moveSelectionTo(range.from)
    this.centerEditorOnPosition(range.from)
  }

  onViewThread(anchorId: string) {
    this.activeCommentPopup$.next(null)
    this.activeCommentAnchorId$.next(anchorId)
    this.sidebarFilter$.next('comments')
    this.editorSettingsService.setIsEditorMaximized(true)
  }

  setSidebarFilter(filter: SidebarFilter) {
    this.sidebarFilter$.next(filter)
  }

  private refreshCommentAnchors() {
    if (this.editor === null) {
      return
    }
    // Viewers never surface text-range comments: the marks stay in the shared
    // document but produce no anchors, so no cards, navigation, or popovers appear.
    // Quoted text is only read for the sidebar cards, so skip it when not maximized.
    const next = this.canUserComment ? collectCommentAnchorSummaries(this.editor.state, this.editorSettingsService.isEditorMaximized) : []
    if (!commentAnchorSummariesEqual(this.commentAnchors$.value, next)) {
      this.commentAnchors$.next(next)
    }
    // Drop the draft flag if its mark was removed by an external edit.
    const draftId = this.activeCommentDraftId$.value
    if (draftId !== null && !next.some((anchor) => anchor.anchorId === draftId)) {
      this.activeCommentDraftId$.next(null)
    }
  }

  private resolveSuggestion(suggestionId: string, action: 'accept' | 'reject', { archiveThread = true }: { archiveThread?: boolean } = {}) {
    if (this.editor === null) {
      return
    }

    const suggestionMarkType = this.editor.state.schema.marks[SUGGESTION_MARK_NAME] ?? null
    if (suggestionMarkType === null) {
      return
    }

    // A locked agenda item rejects the transaction below, so resolving would archive the thread and leave the
    // suggestion in the document with nothing left to act on it.
    if (coversLockedRange(this.editor.state, findAllRangesForSuggestion(this.editor.state, suggestionId))) {
      return
    }

    if (action === 'reject') {
      this.invertListChangeSuggestions(suggestionId)
    }

    const ranges = findAllRangesForSuggestion(this.editor.state, suggestionId)
    if (ranges.length === 0) {
      return
    }

    const transaction = this.editor.state.tr
    const sortedRanges = [...ranges].sort((rangeA, rangeB) => rangeB.from - rangeA.from)
    const schema = this.editor.state.schema

    for (const range of sortedRanges) {
      // A single suggestionId may cover insertion, deletion, and formatting ranges;
      // each range is resolved against its own type, not the suggestion's first range.
      if (range.type === SUGGESTION_TYPE_FORMATTING) {
        if (action === 'reject' && range.formattingChanges !== null) {
          // Undo the tracked formatting change before dropping the suggestion mark.
          for (const markName of range.formattingChanges.added) {
            const markType = schema.marks[markName] ?? null
            if (markType !== null) {
              transaction.removeMark(range.from, range.to, markType)
            }
          }
          for (const markName of range.formattingChanges.removed) {
            const markType = schema.marks[markName] ?? null
            if (markType !== null) {
              transaction.addMark(range.from, range.to, markType.create())
            }
          }
          const colorChange = range.formattingChanges.colorChange
          if (colorChange !== undefined) {
            const textStyleType = schema.marks[TEXT_STYLE_MARK_NAME] ?? null
            if (textStyleType !== null) {
              transaction.removeMark(range.from, range.to, textStyleType)
              if (colorChange.from !== null) {
                transaction.addMark(range.from, range.to, textStyleType.create({ [COLOR_ATTRIBUTE_NAME]: colorChange.from }))
              }
            }
          }
        }
        transaction.removeMark(range.from, range.to, suggestionMarkType)
        continue
      }

      const removeMark = (action === 'accept' && range.type === SUGGESTION_TYPE_INSERTION) || (action === 'reject' && range.type === SUGGESTION_TYPE_DELETION)
      if (removeMark) {
        transaction.removeMark(range.from, range.to, suggestionMarkType)
      } else {
        const deletion = range.type === SUGGESTION_TYPE_DELETION ? expandDeletionToEmptiedListItem(transaction.doc, range) : range
        transaction.delete(deletion.from, deletion.to)
      }
    }

    transaction.setMeta(trackChangesPluginKey, TRACK_CHANGES_SKIP_META)
    this.editor.view.dispatch(transaction)
    this.refreshActiveSuggestion(null)

    if (archiveThread) {
      this.editorThreadsService.archiveThread(this.call.id, 'SUGGESTION', suggestionId, this.callEntityId).catch((error) => {
        this.logger.error('Failed to archive suggestion thread', error)
      })
    }
  }

  // Inverts every listChange entry on the suggestion's formatting ranges, applied
  // in reverse order so the most recent change is rolled back first. Each inversion
  // dispatches its own transaction with TRACK_CHANGES_SKIP_META; positions are
  // re-resolved before each step in case prior inversions shifted the range.
  // The iteration cap is a safety net — each step is supposed to drop one entry,
  // so we never need more iterations than the largest possible listChanges length
  // we could see. A standalone count keeps an unexpected no-op step from looping.
  private static readonly LIST_INVERSION_ITERATION_CAP = 64

  private invertListChangeSuggestions(suggestionId: string) {
    if (this.editor === null) {
      return
    }

    for (let iteration = 0; iteration < TiptapEditorComponent.LIST_INVERSION_ITERATION_CAP; iteration++) {
      const target = this.findNextListChangeInversionTarget(suggestionId)
      if (target === null) {
        return
      }
      this.dispatchStructuralInversion(suggestionId, target.rangeFrom, target.listChange)
      this.dispatchListChangesRewrite(suggestionId, target.remainingListChanges)
    }
  }

  // Returns the union range of all formatting suggestion ranges (with non-empty
  // listChanges) for the given suggestionId, plus the most-recent listChange entry
  // and the remaining ones.
  private findNextListChangeInversionTarget(
    suggestionId: string
  ): { rangeFrom: number; rangeTo: number; listChange: ListChange; remainingListChanges: ListChange[] } | null {
    if (this.editor === null) {
      return null
    }
    const ranges = findAllRangesForSuggestion(this.editor.state, suggestionId)
    let rangeFrom = Number.POSITIVE_INFINITY
    let rangeTo = Number.NEGATIVE_INFINITY
    let listChanges: ListChange[] | null = null
    for (const range of ranges) {
      if (range.type !== SUGGESTION_TYPE_FORMATTING) {
        continue
      }
      const candidates = range.formattingChanges?.listChanges ?? []
      if (candidates.length === 0) {
        continue
      }
      // Every formatting range under one suggestionId carries the same listChanges
      // array, so we read it once and span our selection across all ranges so
      // multi-item lists get inverted in one go.
      listChanges = listChanges ?? candidates
      if (range.from < rangeFrom) {
        rangeFrom = range.from
      }
      if (range.to > rangeTo) {
        rangeTo = range.to
      }
    }
    if (listChanges === null) {
      return null
    }
    const lastIndex = listChanges.length - 1
    return {
      rangeFrom,
      rangeTo,
      listChange: listChanges[lastIndex],
      remainingListChanges: listChanges.slice(0, lastIndex),
    }
  }

  private dispatchStructuralInversion(suggestionId: string, rangeFrom: number, listChange: ListChange) {
    if (listChange.kind === 'wrap') {
      // `wrap` introduced both a list and a listItem; lift twice in one chain.
      this.dispatchInversionChain(rangeFrom, (chain) => chain.command(liftListItemSingleLevel).command(liftListItemSingleLevel))
      return
    }
    if (listChange.kind === 'unwrap') {
      this.dispatchInversionChain(rangeFrom, (chain) => chain.wrapInList(listChange.listType))
      return
    }
    if (listChange.kind === 'changeListType') {
      this.dispatchInversionChain(rangeFrom, (chain) =>
        chain.command(({ state, tr, dispatch }) => {
          const targetType = state.schema.nodes[listChange.from] ?? null
          if (targetType === null) {
            return false
          }
          return swapListType(state, dispatch, tr, listChange.to, targetType)
        })
      )
      return
    }
    if (listChange.source !== undefined) {
      const source = listChange.source
      this.dispatchInversionChain(rangeFrom, (chain) => chain.command(restoreListItemAtSource(source)))
      return
    }
    // Older suggestions captured before sourcePosition was recorded fall back to
    // per-level steps. Mirror the original direction: positive delta (indent
    // applied via sink) inverts with lift; negative delta (outdent applied via
    // lift) inverts with sink. Each step may move the item, so re-resolve the range.
    const count = Math.abs(listChange.delta)
    const invertStep: (chain: ChainedCommands) => ChainedCommands =
      listChange.delta > 0 ? (chain) => chain.command(liftListItemSingleLevel) : (chain) => chain.sinkListItem(LIST_ITEM_NODE_NAME)
    for (let index = 0; index < count; index++) {
      const range = this.firstFormattingRangeForSuggestion(suggestionId)
      if (range === null) {
        return
      }
      const dispatched = this.dispatchInversionChain(range.from, invertStep)
      if (!dispatched) {
        return
      }
    }
  }

  private dispatchInversionChain(rangeFrom: number, applyChain: (chain: ChainedCommands) => ChainedCommands): boolean {
    if (this.editor === null) {
      return false
    }
    // Place a cursor at the start of the range. PM list commands consult the selection
    // to find the listItem ancestor; a cursor produces a single, well-defined block
    // range, while a wide selection can confuse `liftListItem` into lifting too far.
    const chain = this.editor.chain().setTextSelection(rangeFrom)
    return applyChain(chain)
      .command(({ tr }) => {
        tr.setMeta(trackChangesPluginKey, TRACK_CHANGES_SKIP_META)
        return true
      })
      .run()
  }

  private firstFormattingRangeForSuggestion(suggestionId: string): { from: number; to: number } | null {
    if (this.editor === null) {
      return null
    }
    let from = Number.POSITIVE_INFINITY
    let to = Number.NEGATIVE_INFINITY
    let found = false
    for (const range of findAllRangesForSuggestion(this.editor.state, suggestionId)) {
      if (range.type !== SUGGESTION_TYPE_FORMATTING) {
        continue
      }
      found = true
      if (range.from < from) {
        from = range.from
      }
      if (range.to > to) {
        to = range.to
      }
    }
    return found ? { from, to } : null
  }

  private dispatchListChangesRewrite(suggestionId: string, remaining: ListChange[]) {
    if (this.editor === null) {
      return
    }
    const transaction = this.editor.state.tr
    this.rewriteSuggestionListChanges(transaction, suggestionId, remaining)
    if (transaction.steps.length === 0) {
      return
    }
    transaction.setMeta(trackChangesPluginKey, TRACK_CHANGES_SKIP_META)
    this.editor.view.dispatch(transaction)
  }

  // Replace the suggestion's formattingChanges.listChanges with `remaining`. When
  // `remaining` is empty AND added/removed are empty, drop the mark entirely so the
  // outer mark-removal pass has nothing to do here.
  private rewriteSuggestionListChanges(tr: Transaction, suggestionId: string, remaining: ListChange[]) {
    if (this.editor === null) {
      return
    }
    const suggestionMarkType = this.editor.state.schema.marks[SUGGESTION_MARK_NAME] ?? null
    if (suggestionMarkType === null) {
      return
    }

    // Touch every text node carrying the mark — sibling nodes that differ in
    // their other inline marks each hold their own suggestion-mark instance,
    // and skipping any of them would leave stale listChanges behind for the
    // next iteration of the inversion loop to find.
    const updates: { from: number; to: number; oldMark: ProseMirrorMark; nextAttrs: Record<string, unknown> | null }[] = []

    tr.doc.descendants((node, pos) => {
      if (!node.isText) {
        return true
      }
      const mark =
        node.marks.find(
          (candidate) =>
            candidate.type.name === SUGGESTION_MARK_NAME &&
            candidate.attrs['suggestionId'] === suggestionId &&
            candidate.attrs['type'] === SUGGESTION_TYPE_FORMATTING
        ) ?? null
      if (mark === null) {
        return false
      }

      const formattingChanges = (mark.attrs['formattingChanges'] as FormattingChanges | null) ?? { added: [], removed: [] }
      const nextFormatting: FormattingChanges = { added: formattingChanges.added, removed: formattingChanges.removed }
      if (formattingChanges.colorChange !== undefined) {
        nextFormatting.colorChange = formattingChanges.colorChange
      }
      if (remaining.length > 0) {
        nextFormatting.listChanges = remaining
      }
      const isEmpty =
        nextFormatting.added.length === 0 &&
        nextFormatting.removed.length === 0 &&
        (nextFormatting.listChanges ?? []).length === 0 &&
        nextFormatting.colorChange === undefined
      const nextAttrs = isEmpty ? null : { ...mark.attrs, formattingChanges: nextFormatting }
      updates.push({ from: pos, to: pos + node.nodeSize, oldMark: mark, nextAttrs })
      return false
    })

    for (const update of updates) {
      tr.removeMark(update.from, update.to, update.oldMark)
      if (update.nextAttrs !== null) {
        tr.addMark(update.from, update.to, suggestionMarkType.create(update.nextAttrs))
      }
    }
  }

  gotoNextChange() {
    this.gotoAdjacentChange('next')
  }

  gotoPreviousChange() {
    this.gotoAdjacentChange('previous')
  }

  viewSuggestion(suggestionId: string) {
    if (this.editor === null) {
      return
    }

    const ranges = findAllRangesForSuggestion(this.editor.state, suggestionId)
    if (ranges.length === 0) {
      return
    }
    const position = ranges[0].from
    const view = this.editor.view
    view.dispatch(view.state.tr.setSelection(TextSelection.create(view.state.doc, position)))
    view.focus()
    this.centerEditorOnPosition(position)
  }

  openSuggestionComments(suggestionId: string) {
    const wasMaximized = this.editorSettingsService.isEditorMaximized
    this.editorSettingsService.setIsEditorMaximized(true)
    if (wasMaximized) {
      this.viewSuggestion(suggestionId)
      return
    }
    // Wait until Angular has rendered the .maximized layout and the browser has
    // laid it out — otherwise `coordsAtPos` / `getBoundingClientRect` in
    // `viewSuggestion` use stale pre-maximize coordinates and the centred scroll
    // lands in the wrong place. Two RAFs: one for change detection, one for paint.
    this.cancelOpenCommentsRafs()
    this.openCommentsRafIds[0] = requestAnimationFrame(() => {
      this.openCommentsRafIds[1] = requestAnimationFrame(() => {
        this.openCommentsRafIds = [null, null]
        if (this.editor !== null) {
          this.viewSuggestion(suggestionId)
        }
      })
    })
  }

  private cancelOpenCommentsRafs() {
    this.openCommentsRafIds.forEach((rafId) => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId)
      }
    })
    this.openCommentsRafIds = [null, null]
  }

  private centerEditorOnPosition(position: number) {
    if (this.editor === null) {
      return
    }
    centerViewOnPosition(this.editor.view, position)
  }

  private gotoAdjacentChange(direction: 'next' | 'previous') {
    if (this.editor === null) {
      return
    }

    const startPositions = this.filteredNavigationPositions()
    if (startPositions.length === 0) {
      return
    }

    const currentPosition = this.editor.state.selection.from
    const target =
      direction === 'next'
        ? (startPositions.find((position) => position > currentPosition) ?? startPositions[0])
        : (startPositions.findLast((position) => position < currentPosition) ?? startPositions[startPositions.length - 1])

    this.moveSelectionTo(target)
  }

  private filteredNavigationPositions(): number[] {
    if (this.editor === null) {
      return []
    }
    const includes = navigationIncludes(this.sidebarFilter$.value)
    const changePositions = includes.changes ? collectSuggestionStartPositions(this.editor.state) : []
    const commentPositions = includes.comments ? collectCommentAnchorStartPositions(this.editor.state) : []
    return [...changePositions, ...commentPositions].sort((left, right) => left - right)
  }

  private moveSelectionTo(position: number) {
    if (this.editor === null) {
      return
    }
    const transaction = this.editor.state.tr.setSelection(TextSelection.create(this.editor.state.doc, position))
    transaction.scrollIntoView()
    this.editor.view.dispatch(transaction)
    this.editor.view.focus()
  }

  private refreshSuggestionCount() {
    if (this.editor === null) {
      return
    }
    const count = collectSuggestionStartPositions(this.editor.state).length
    if (this.suggestionCount$.value !== count) {
      this.suggestionCount$.next(count)
    }
  }

  private refreshSuggestions() {
    if (this.editor === null) {
      return
    }
    const next = this.editorSettingsService.isEditorMaximized ? collectAllSuggestions(this.editor.state) : []
    if (suggestionSummariesEqual(this.suggestions$.value, next)) {
      return
    }
    this.suggestions$.next(next)
  }

  private refreshActiveSuggestion(next: ActiveSuggestion | null) {
    const current = this.activeSuggestion$.value

    if (current === null && next === null) {
      return
    }
    if (
      current !== null &&
      next !== null &&
      current.suggestionId === next.suggestionId &&
      current.authorName === next.authorName &&
      current.createdAt === next.createdAt &&
      suggestionRunsEqual(current.insertionRuns, next.insertionRuns) &&
      suggestionRunsEqual(current.deletionRuns, next.deletionRuns) &&
      stringArraysEqual(current.formattingAdded, next.formattingAdded) &&
      stringArraysEqual(current.formattingRemoved, next.formattingRemoved) &&
      listChangeArraysEqual(current.listChanges, next.listChanges) &&
      colorChangesEqual(current.colorChange, next.colorChange)
    ) {
      return
    }

    this.activeSuggestion$.next(next)
  }

  private findCommentAnchorAtCursor(props: { state: EditorState; oldState?: EditorState; from: number; to: number }): string | null {
    if (this.editor === null || props.from !== props.to) {
      return null
    }
    const oldState = props.oldState ?? null
    if (oldState !== null && !oldState.doc.eq(props.state.doc)) {
      return null
    }
    return findCommentAnchorIdAtPosition(props.state, props.from)
  }

  private refreshActiveCommentAnchor(anchorId: string | null) {
    if (this.activeCommentAnchorId$.value !== anchorId) {
      this.activeCommentAnchorId$.next(anchorId)
    }
  }

  toggleBoldOnSelection() {
    this.editor?.chain().focus().toggleBold().run()
  }

  toggleItalicOnSelection() {
    this.editor?.chain().focus().toggleItalic().run()
  }

  toggleHighlightOnSelection() {
    this.editor?.chain().focus().toggleHighlight().run()
  }

  applyCurrentTextColor() {
    if (this.editor === null) {
      return
    }
    runColorCommandWithTracking(this.editor, this.trackChangesCommandOptions(), this.currentTextColor)
  }

  selectTextColor(color: string) {
    this.currentTextColor = color
    this.applyCurrentTextColor()
  }

  /** The agenda's own items are numbered and the attendee list is bulleted, both by definition rather than choice. */
  get bulletListDisabled(): boolean {
    return this.editor !== null && (isMinuteTreeListTypeLocked(this.editor.state) || isAttendeeListSelected(this.editor.state))
  }

  /** The attendee list is bulleted throughout, and unlike the minutes tree that holds for its subitems too. */
  get orderedListDisabled(): boolean {
    return this.editor !== null && isAttendeeListSelected(this.editor.state)
  }

  /** The attendee list is two levels deep by definition, so there is nothing in it left to indent into. */
  get increaseIndentDisabled(): boolean {
    return this.editor !== null && isAttendeeListSelected(this.editor.state)
  }

  toggleBulletList() {
    this.runTrackedListCommand((chain) => chain.toggleBulletList())
  }

  toggleOrderedList() {
    this.runTrackedListCommand((chain) => chain.toggleOrderedList())
  }

  increaseIndent() {
    this.runTrackedListCommand((chain) => chain.sinkListItem(LIST_ITEM_NODE_NAME))
  }

  decreaseIndent() {
    this.runTrackedListCommand((chain) => chain.command(liftListItemSingleLevel))
  }

  private trackChangesCommandOptions() {
    return {
      getEnabled: () => this.trackChangesEnabled$.value,
      getCurrentUser: () => {
        const currentUser = this.usersService.currentUser$.value
        if (currentUser === null) {
          return null
        }
        return { id: currentUser.id, name: constructNameForUser(currentUser) }
      },
    }
  }

  private runTrackedListCommand(applyCommand: (chain: ChainedCommands) => ChainedCommands) {
    if (this.editor === null) {
      return
    }
    runListCommandWithTracking(this.editor, this.trackChangesCommandOptions(), applyCommand)
  }

  private handleOpenMeeting(event: OpenMeetingEvent) {
    openCitedMeeting(this.router, this.editorSettingsService, event)
    this.openMeeting.emit(event)
  }

  async performAiAction(action: LlmAction) {
    if (this.editor === null) {
      return
    }

    const actionConfig = AI_ACTION_CONFIGS.find((config) => config.action === action) ?? null
    if (actionConfig === null) {
      return
    }

    try {
      expandSelectionToWordBoundaries(this.editor)
    } catch (error) {
      this.toastService.show('EDITOR.AI_ACTION_FAILED')
      this.logger.error('Failed to expand selection to word boundaries', error)
      return
    }

    const { from, to } = this.editor.state.selection
    const selectedText = getSelectedText(this.editor)

    if (selectedText === null) {
      this.toastService.show('EDITOR.AI_ACTION_FAILED')
      return
    }

    const sentenceIdPairs = getSentenceIdPairsFromSelection(this.editor, this.call.outlineItems)
    const templateItemIds = getTemplateItemIdsFromSelection(this.editor)
    const existingItemContent = getExistingItemContent(this.editor)
    const actionDisplayName = this.translateService.instant(actionConfig.i18nKey)

    this.scrollTranscriptToSentences(sentenceIdPairs)

    try {
      if (actionConfig.stream) {
        await this.handleStreamingAction(action, actionDisplayName, selectedText, sentenceIdPairs, templateItemIds, existingItemContent, from, to)
      } else {
        await this.handleSyncAction(action, actionDisplayName, selectedText, sentenceIdPairs, templateItemIds, existingItemContent, from, to)
      }
    } catch (error) {
      this.toastService.show('EDITOR.AI_ACTION_FAILED')
      this.logger.error('AI action failed', error)
    }
  }

  private async handleStreamingAction(
    action: LlmAction,
    actionDisplayName: string,
    selectedText: string,
    sentenceIdPairs: SentenceIdPair[],
    templateItemIds: number[],
    existingItemContent: string | null,
    selectionFrom: number,
    selectionTo: number
  ) {
    const dialogData: AiActionStreamingDialogData = {
      callId: this.call.id,
      action,
      actionDisplayName,
      originalContent: selectedText,
      sentenceIdPairs,
      templateItemIds,
      existingItemContent,
    }

    const dialogRef = this.dialog.open(AiActionStreamingDialogComponent, {
      data: dialogData,
      hasBackdrop: false,
      autoFocus: false,
      restoreFocus: false,
      panelClass: 'ai-action-dialog-panel',
    })

    const result = await lastValueFrom(dialogRef.afterClosed())

    if (result !== null && result !== undefined && this.editor !== null) {
      this.metricsService.sendMetric(this.call.id, MetricType.CLICK_LLM_LIVE_ACTION_INSERTION_OF_SUGGESTION)
      this.replaceRangeBypassingTrackChanges(selectionFrom, selectionTo, result.generatedText)
    } else {
      this.editor?.commands.focus(null, { scrollIntoView: false })
    }
  }

  private async handleSyncAction(
    action: LlmAction,
    actionDisplayName: string,
    selectedText: string,
    sentenceIdPairs: SentenceIdPair[],
    templateItemIds: number[],
    existingItemContent: string | null,
    selectionFrom: number,
    selectionTo: number
  ) {
    const dialogData: AiActionFactCheckDialogData = {
      callId: this.call.id,
      action,
      actionDisplayName,
      selectionContent: selectedText,
      sentenceIdPairs,
      templateItemIds,
      existingItemContent,
      onOpenDocument: (event) => this.openDocument.emit(event),
      onOpenMeeting: (event) => this.handleOpenMeeting(event),
    }

    const dialogRef = this.dialog.open(AiActionFactCheckDialogComponent, {
      data: dialogData,
      hasBackdrop: false,
      autoFocus: false,
      restoreFocus: false,
      panelClass: 'ai-action-dialog-panel',
    })

    const result = await lastValueFrom(dialogRef.afterClosed())

    if (result !== null && result !== undefined && this.editor !== null) {
      this.metricsService.sendMetric(this.call.id, MetricType.CLICK_LLM_LIVE_ACTION_INSERTION_OF_SUGGESTION)
      this.replaceRangeBypassingTrackChanges(selectionFrom, selectionTo, result.insertionText)
    } else {
      this.editor?.commands.focus(null, { scrollIntoView: false })
    }
  }

  // The text before `from` is left as it is, so pinning `from` to its place on screen leaves only what follows the
  // insertion to move.
  private replaceRangeBypassingTrackChanges(from: number, to: number, content: string) {
    if (this.editor === null) {
      return
    }
    const view = this.editor.view
    const scrollElement = this.resolveScrollElement() ?? null
    const topBeforeInsertion = view.coordsAtPos(from).top

    this.editor
      .chain()
      .focus(null, { scrollIntoView: false })
      .command(({ tr }) => {
        tr.setMeta(trackChangesPluginKey, TRACK_CHANGES_SKIP_META)
        return true
      })
      .insertContentAt({ from, to }, content)
      .command(({ tr }) => {
        pulseInsertedRange(tr, tr.mapping.map(from, -1), tr.mapping.map(to, 1))
        return true
      })
      .run()

    if (scrollElement !== null) {
      scrollElement.scrollTop += view.coordsAtPos(from).top - topBeforeInsertion
    }
  }

  private smoothScrollToSelection(view: EditorView): boolean {
    const container = findScrollableAncestor(view.dom as HTMLElement)
    if (container === null) {
      return false
    }
    const coords = view.coordsAtPos(view.state.selection.head)
    const rect = container.getBoundingClientRect()
    const margin = 64
    let target = container.scrollTop
    if (coords.bottom > rect.bottom - margin) {
      target += coords.bottom - rect.bottom + margin
    } else if (coords.top < rect.top + margin) {
      target += coords.top - rect.top - margin
    } else {
      return true
    }
    container.scrollTo({ top: Math.max(0, target), behavior: 'smooth' })
    return true
  }

  private scrollToTemplateItem(templateItemId: number) {
    console.debug(`[TipTapEditor] Scrolling to minute node with template item ID ${templateItemId}`)

    if (this.editor === null) {
      return
    }

    const element = this.editor.view.dom.querySelector(`[data-template-item-id="${templateItemId}"]`)

    if (element !== null) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' })
    } else {
      console.warn(`[TipTapEditor] Could not find element to scroll to with selector [data-template-item-id="${templateItemId}"]`)
    }
  }

  private scrollTranscriptToSentences(sentenceIdPairs: SentenceIdPair[]) {
    if (sentenceIdPairs.length === 0) {
      return
    }

    const startSentenceId = sentenceIdPairs[0].startSentenceId
    const endSentenceId = sentenceIdPairs[sentenceIdPairs.length - 1].endSentenceId

    this.router.navigate([], {
      queryParams: { sentenceId: startSentenceId, endSentenceId, highlight: true },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    })
  }
}
